import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './Chatbot.css';

const SYSTEM_PROMPT = `You are OceanSense AI — a helpful, knowledgeable ocean conservation assistant. You help people with:

1. **Marine emergencies**: Stranded animals, oil spills, injured wildlife — provide step-by-step rescue instructions and emergency contacts.
2. **Ocean knowledge**: Marine biology, species identification, ecosystem facts.
3. **Coastal safety**: Beach safety, tide info, weather warnings, swimming advisories.
4. **Environmental problems**: Pollution reporting guidance, plastic reduction tips, sustainability advice.
5. **Conservation action**: How to volunteer, organize cleanups, report illegal fishing/dumping.
6. **Government schemes**: Relevant coastal/marine welfare programs.

Important guidelines:
- Always be helpful, empathetic, and action-oriented.
- For emergencies, ALWAYS provide relevant Indian emergency numbers: Coast Guard (1554), Marine Police, Disaster Management (1078), Animal rescue helplines.
- Keep responses concise but thorough. Use bullet points for step-by-step instructions.
- If someone reports a problem, guide them on WHO to contact and HOW to report.
- For species questions, provide habitat, conservation status, and interesting facts.
- Format responses with markdown: use **bold** for key terms, bullet points for lists.`;

const QUICK_PROMPTS = [
  { emoji: '🐢', text: 'Found a stranded animal' },
  { emoji: '🚨', text: 'Report pollution' },
  { emoji: '🌊', text: 'Is it safe to swim today?' },
  { emoji: '♻️', text: 'Reduce my plastic footprint' },
  { emoji: '🐟', text: 'Identify a fish species' },
  { emoji: '📞', text: 'Emergency contacts' },
];

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "👋 Hi! I'm **OceanSense AI** — your ocean conservation assistant.\n\nI can help you with:\n* 🐢 Marine animal rescue\n* 🚨 Reporting pollution\n* 🌊 Beach & coastal safety\n* 🐟 Marine species info\n* ♻️ Sustainability tips\n* 📞 Emergency contacts\n\nHow can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const formatMessage = (text) => {
    // Convert markdown-like formatting to HTML
    let html = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)(?:\n|$)/g, '<li>$1</li>')
      .replace(/\n/g, '<br/>');
    
    // Wrap consecutive <li> items in <ul>
    html = html.replace(/((?:<li>.*?<\/li><br\/>?)+)/g, '<ul>$1</ul>');
    
    return html;
  };

  const sendMessage = async (messageText) => {
    const text = messageText || input.trim();
    if (!text) return;

    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);

    try {
      const groqApiKey = process.env.REACT_APP_GROQ_API_KEY;
      
      // Build conversation history (last 10 messages for context)
      const conversationHistory = updatedMessages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await axios.post(
        'https://api.groq.com/openai/v1/chat/completions',
        {
          model: 'llama-3.3-70b-versatile',
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            ...conversationHistory
          ],
          temperature: 0.7,
          max_tokens: 800,
        },
        {
          headers: {
            'Authorization': `Bearer ${groqApiKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const aiResponse = response.data.choices[0].message.content;
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Sorry, I encountered an error. Please try again. If this persists, you can call the **Coast Guard at 1554** for immediate help.'
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Window */}
      <div className={`chatbot-window ${isOpen ? 'open' : ''}`}>
        <div className="chatbot-header">
          <div className="chatbot-header-info">
            <div className="chatbot-avatar-header">🌊</div>
            <div>
              <h3>OceanSense AI</h3>
              <span className="chatbot-status">
                <span className="status-dot"></span>
                Always online
              </span>
            </div>
          </div>
          <button className="chatbot-close" onClick={() => setIsOpen(false)}>✕</button>
        </div>

        <div className="chatbot-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chat-message ${msg.role}`}>
              {msg.role === 'assistant' && <div className="msg-avatar">🌊</div>}
              <div 
                className="msg-bubble" 
                dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
              />
            </div>
          ))}

          {isTyping && (
            <div className="chat-message assistant">
              <div className="msg-avatar">🌊</div>
              <div className="msg-bubble typing">
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts */}
        {messages.length <= 1 && (
          <div className="quick-prompts">
            {QUICK_PROMPTS.map((prompt, i) => (
              <button key={i} className="quick-prompt" onClick={() => sendMessage(prompt.text)}>
                <span>{prompt.emoji}</span> {prompt.text}
              </button>
            ))}
          </div>
        )}

        <div className="chatbot-input-area">
          <input
            ref={inputRef}
            type="text"
            placeholder="Ask anything about oceans..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isTyping}
          />
          <button 
            className="send-btn" 
            onClick={() => sendMessage()} 
            disabled={!input.trim() || isTyping}
          >
            ➤
          </button>
        </div>
      </div>

      {/* Floating Bubble */}
      <button 
        className={`chatbot-bubble ${isOpen ? 'hidden' : ''}`} 
        onClick={() => setIsOpen(true)}
        aria-label="Open chat assistant"
      >
        <span className="bubble-icon">🌊</span>
        <span className="bubble-pulse"></span>
        {unreadCount > 0 && <span className="bubble-badge">{unreadCount}</span>}
      </button>
    </>
  );
};

export default Chatbot;
